
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, Calendar, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

interface SubscriptionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: 'supabase' | 'internal';
  view: 'renewals' | 'invoices';
}

const SubscriptionHistoryModal: React.FC<SubscriptionHistoryModalProps> = ({ 
  isOpen, 
  onClose, 
  userType,
  view 
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  const renewalDates = [
    {
      id: '1',
      date: '2024-08-15',
      plan: 'Coach Plan',
      amount: '$9.99',
      status: 'upcoming'
    },
    {
      id: '2',
      date: '2024-07-15',
      plan: 'Coach Plan',
      amount: '$9.99',
      status: 'completed'
    },
    {
      id: '3',
      date: '2024-06-15',
      plan: 'Coach Plan',
      amount: '$9.99',
      status: 'completed'
    }
  ];

  const invoices = [
    {
      id: 'INV-001',
      date: '2024-07-15',
      amount: '$9.99',
      status: 'paid',
      plan: 'Coach Plan Monthly',
      downloadUrl: '#'
    },
    {
      id: 'INV-002',
      date: '2024-06-15',
      amount: '$9.99',
      status: 'paid',
      plan: 'Coach Plan Monthly',
      downloadUrl: '#'
    },
    {
      id: 'INV-003',
      date: '2024-05-15',
      amount: '$9.99',
      status: 'paid',
      plan: 'Coach Plan Monthly',
      downloadUrl: '#'
    }
  ];

  const handleDownload = (invoiceId: string) => {
    // In a real implementation, this would generate and download the actual invoice
    toast.success(`Downloading invoice ${invoiceId}...`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-600/20 text-blue-400 border-blue-600/30';
      case 'completed':
      case 'paid':
        return 'bg-green-600/20 text-green-400 border-green-600/30';
      case 'failed':
        return 'bg-red-600/20 text-red-400 border-red-600/30';
      default:
        return 'bg-gray-600/20 text-gray-400 border-gray-600/30';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            {view === 'renewals' ? 'Renewal Dates' : 'Invoice History'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Filter Options */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={selectedPeriod === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('all')}
              className={selectedPeriod === 'all' 
                ? 'bg-sports-green text-black' 
                : 'border-gray-600 text-gray-300 hover:bg-gray-700'
              }
            >
              All Time
            </Button>
            <Button
              size="sm"
              variant={selectedPeriod === '3months' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('3months')}
              className={selectedPeriod === '3months' 
                ? 'bg-sports-green text-black' 
                : 'border-gray-600 text-gray-300 hover:bg-gray-700'
              }
            >
              Last 3 Months
            </Button>
            <Button
              size="sm"
              variant={selectedPeriod === '6months' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('6months')}
              className={selectedPeriod === '6months' 
                ? 'bg-sports-green text-black' 
                : 'border-gray-600 text-gray-300 hover:bg-gray-700'
              }
            >
              Last 6 Months
            </Button>
          </div>

          {/* Content */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {view === 'renewals' ? (
              // Renewal Dates View
              renewalDates.map((renewal) => (
                <Card key={renewal.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-sports-green" />
                        <div>
                          <p className="text-white font-medium">{renewal.plan}</p>
                          <p className="text-gray-400 text-sm">
                            {new Date(renewal.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{renewal.amount}</span>
                        <Badge className={getStatusColor(renewal.status)}>
                          {renewal.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              // Invoices View
              invoices.map((invoice) => (
                <Card key={invoice.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-sports-green" />
                        <div>
                          <p className="text-white font-medium">{invoice.id}</p>
                          <p className="text-gray-400 text-sm">{invoice.plan}</p>
                          <p className="text-gray-500 text-xs">
                            {new Date(invoice.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{invoice.amount}</span>
                        <Badge className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(invoice.id)}
                          className="border-sports-green text-sports-green hover:bg-sports-green/10"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <p className="text-xs text-gray-500 text-center">
            {userType === 'supabase' 
              ? `Old School account ${view === 'renewals' ? 'renewal dates' : 'invoices'}` 
              : `New School account ${view === 'renewals' ? 'renewal dates' : 'invoices'}`
            }
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionHistoryModal;
